/**
 * Knex implementation of IDagRepository.
 *
 * Extends KnexTreeRepository with DAG (Directed Acyclic Graph) semantics.
 * Key difference from Tree: parent relationships are stored in a join table
 * instead of a `parent_id` column. A node may have multiple parents.
 *
 * Join table convention (customizable via options):
 *   - Default name: `${table}_links`
 *   - child_id column: references the child node (configurable via `childField`)
 *   - parent_id column: references the parent node (configurable via `parentField`)
 *
 * NOTE: The `parentField` inherited from KnexTreeRepository refers to the
 * parent column in the join table, NOT a column in the entity table.
 * The entity table itself has no parent_id column in DAG mode.
 */

import type { Knex } from 'knex'
import type { IDagRepository } from '../types.js'
import { KnexTreeRepository, type KnexTreeRepoOptions } from './tree.repo.js'
import { handleDbError } from '../../../db/knex-adapter.js'

export interface KnexDagRepoOptions extends Omit<KnexTreeRepoOptions, 'parentField'> {
  /** Join table name. Default: `${table}_links`. */
  linkTable?: string
  /** Column for child id in join table. Default: 'child_id'. */
  childField?: string
  /** Column for parent id in join table. Default: 'parent_id'. */
  parentField?: string
}

export class KnexDagRepository<T extends Record<string, unknown> = Record<string, unknown>>
  extends KnexTreeRepository<T>
  implements IDagRepository<T>
{
  protected readonly linkTable: string
  protected readonly childField: string

  constructor(knex: Knex, table: string, options: KnexDagRepoOptions = {}) {
    // Pass parentField to parent class — it will be stored as this.parentField
    // but used here exclusively for the join table column name (not entity table).
    super(knex, table, { ...options, parentField: options.parentField ?? 'parent_id' })
    this.linkTable = options.linkTable ?? `${table}_links`
    this.childField = options.childField ?? 'child_id'
  }

  // ---------------------------------------------------------------------------
  // IDagRepository — DAG-specific methods
  // ---------------------------------------------------------------------------

  /**
   * Get all parent nodes of a given node via the join table.
   *
   * @param nodeId - Child node ID.
   */
  async getParents(nodeId: string): Promise<T[]> {
    return this.knex(this.table)
      .join(
        this.linkTable,
        `${this.table}.id`,
        `${this.linkTable}.${this.parentField}`
      )
      .where(`${this.linkTable}.${this.childField}`, nodeId)
      .select(`${this.table}.*`) as unknown as Promise<T[]>
  }

  /**
   * Replace all parents of a node in a single transaction.
   * Performs cycle validation before entering the transaction.
   *
   * @param nodeId    - Node whose parents will be replaced.
   * @param parentIds - New set of parent IDs (empty = make root).
   * @throws Error if any parentId would create a cycle.
   */
  async setParents(nodeId: string, parentIds: string[]): Promise<void> {
    // Cycle validation outside transaction (reads are safe here)
    const descendants = await this.getDescendants(nodeId)
    const descendantIds = new Set(descendants.map((d) => d['id'] as string))

    for (const pid of parentIds) {
      if (pid === nodeId) {
        throw new Error('Cannot set node as its own parent')
      }
      if (descendantIds.has(pid)) {
        throw new Error(`Cannot set parent '${pid}': would create a cycle`)
      }
    }

    try {
      await this.knex.transaction(async (trx) => {
        await trx(this.linkTable).where(this.childField, nodeId).delete()

        if (parentIds.length > 0) {
          await trx(this.linkTable).insert(
            parentIds.map((pid) => ({
              [this.childField]: nodeId,
              [this.parentField]: pid,
            }))
          )
        }
      })
    } catch (e) {
      handleDbError(e)
    }
  }

  /**
   * Add a single parent to a node. Idempotent — silently ignores duplicate links.
   *
   * @param nodeId   - Node to add the parent to.
   * @param parentId - Parent node ID.
   * @throws Error if nodeId === parentId or if it would create a cycle.
   */
  async addParent(nodeId: string, parentId: string): Promise<void> {
    if (nodeId === parentId) {
      throw new Error('Cannot add node as its own parent')
    }

    // Cycle check: parentId must not be a descendant of nodeId
    const descendants = await this.getDescendants(nodeId)
    if (descendants.some((d) => d['id'] === parentId)) {
      throw new Error(`Cannot add parent '${parentId}': would create a cycle`)
    }

    // Idempotency check — skip insert if link already exists
    const existing = await this.knex(this.linkTable)
      .where(this.childField, nodeId)
      .where(this.parentField, parentId)
      .first()

    if (existing) return

    try {
      await this.knex(this.linkTable).insert({
        [this.childField]: nodeId,
        [this.parentField]: parentId,
      })
    } catch (e) {
      handleDbError(e)
    }
  }

  /**
   * Remove a parent link. No-op if the link does not exist.
   *
   * @param nodeId   - Child node ID.
   * @param parentId - Parent node ID to remove.
   */
  async removeParent(nodeId: string, parentId: string): Promise<void> {
    try {
      await this.knex(this.linkTable)
        .where(this.childField, nodeId)
        .where(this.parentField, parentId)
        .delete()
    } catch (e) {
      handleDbError(e)
    }
  }

  // ---------------------------------------------------------------------------
  // Overrides of KnexTreeRepository — join-table semantics
  // ---------------------------------------------------------------------------

  /**
   * Root nodes: nodes that do not appear as a child in the join table.
   */
  override async findRoots(): Promise<T[]> {
    return this.knex(this.table)
      .whereNotIn('id', this.knex(this.linkTable).select(this.childField))
      .select('*') as unknown as Promise<T[]>
  }

  /**
   * Direct children of a parent node, or roots if parentId is null.
   *
   * @param parentId - Parent node ID, or null for root nodes.
   */
  override async findChildren(parentId: string | null): Promise<T[]> {
    if (parentId === null) {
      return this.findRoots()
    }

    return this.knex(this.table)
      .join(
        this.linkTable,
        `${this.table}.id`,
        `${this.linkTable}.${this.childField}`
      )
      .where(`${this.linkTable}.${this.parentField}`, parentId)
      .select(`${this.table}.*`) as unknown as Promise<T[]>
  }

  /**
   * All ancestors of a node via BFS (multiple parent paths flattened, no duplicates).
   * Does not include the node itself.
   *
   * @param id - Node ID.
   */
  override async getAncestors(id: string): Promise<T[]> {
    const result = new Map<string, T>()
    const visited = new Set<string>()
    const frontier = [id]

    while (frontier.length > 0) {
      const current = frontier.shift()!
      if (visited.has(current)) continue
      visited.add(current)

      const parents = await this.getParents(current)
      for (const parent of parents) {
        const pid = parent['id'] as string
        if (!result.has(pid)) {
          result.set(pid, parent)
          frontier.push(pid)
        }
      }
    }

    return Array.from(result.values())
  }

  /**
   * All descendants via BFS (no duplicates). Does not include the node itself.
   *
   * @param id - Node ID.
   */
  override async getDescendants(id: string): Promise<T[]> {
    const result = new Map<string, T>()
    const visited = new Set<string>([id])
    const queue = [id]

    while (queue.length > 0) {
      const current = queue.shift()!
      const children = await this.findChildren(current)

      for (const child of children) {
        const cid = child['id'] as string
        if (!visited.has(cid)) {
          visited.add(cid)
          result.set(cid, child)
          queue.push(cid)
        }
      }
    }

    return Array.from(result.values())
  }

  /**
   * Move a node to a new parent (DAG interpretation: replace ALL parents).
   * Equivalent to setParents(id, newParentId === null ? [] : [newParentId]).
   *
   * @param id          - Node to move.
   * @param newParentId - New single parent, or null to make root.
   * @returns Updated node (unchanged entity row).
   */
  override async move(id: string, newParentId: string | null): Promise<T> {
    await this.setParents(id, newParentId === null ? [] : [newParentId])
    return (await this.findById(id)) as T
  }
}
